import parseISO from "date-fns/parseISO";
import getYear from "date-fns/getYear";
import format from "date-fns/format";
import type { InternalRefetchQueriesInclude } from "@apollo/client";
import { ArrowLongRightIcon, CheckIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ClipboardDocumentIcon } from "@heroicons/react/24/solid";
import Avatar from "components/core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider, MenuTitle } from "components/core/menu";
import CommentActionsDropdown from "components/discussions/CommentActionsDropdown";
import Editor from "components/editor/Editor";
import ForumEditPost from "components/forum/FormEditPost";
import FormEditPostTitle from "components/forum/FormEditPostTitle";
import usePostActions from "components/forum/hooks/usePostActions";
import PostReactions from "components/reactions/PostReactions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/Dialog";
import RelativeTime from "components/ui/RelativeTime";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import ptBR from "date-fns/locale/pt-BR";
import type { IPostReplyFieldsFragment } from "lib/graphql/fragments/engagement/__generated__/PostReplyFields.generated";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import PostsQuery from "lib/graphql/queries/engagement/PostsQuery";
import type { IPostRepliesQuery } from "lib/graphql/queries/engagement/__generated__/PostRepliesQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import { PencilIcon, Pin, Reply } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import TextareaAutosize from "react-autosize-textarea/lib";
import { IReactionSubject } from "types/graphqlTypes";
import { PostContextType } from "types/typedDocumentNodes";
interface IForumContainerProps {
  children: React.ReactNode;
}

export function ForumContainer({ children }: IForumContainerProps) {
  return (
    <div
      className={classNames(
        "mx-auto grid w-full max-w-7xl grid-cols-4 gap-x-4 gap-y-6 px-4 pb-6",
        "xl:grid-cols-12 xl:gap-9 xl:px-0",
      )}
    >
      {children}
    </div>
  );
}

interface IForumQuestionTitleProps {
  children: React.ReactNode;
  topic: NonNullable<IPostRepliesQuery["postByShortId"]>;
}

export function ForumQuestionTitle({ children, topic }: IForumQuestionTitleProps) {
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const selfie = useSelfie();

  const viewerCanEditTitle = topic.viewerDidAuthor || (selfie && selfie.isSiteAdmin);

  return (
    <div className="col-span-4 space-y-0.5 font-aeonik md:col-span-12">
      <div className="flex items-center space-x-3">
        <h1 className="m-0 text-2xl font-medium lg:text-3xl">{children}</h1>

        {viewerCanEditTitle && (
          <Dialog open={isEditingTitle} onOpenChange={setIsEditingTitle}>
            <DialogTrigger>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild={true}>
                    <PencilIcon className="h-5 w-5 text-gray-500 hover:text-brand" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={12}
                    className="max-w-xs rounded-xl border border-transparent bg-black text-white"
                  >
                    <strong className="font-bold">Editar Título</strong>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTrigger>

            <DialogContent className="max-h-screen sm:max-w-[425px] lg:max-w-3xl">
              <DialogHeader>
                <DialogTitle>Editar Título</DialogTitle>
              </DialogHeader>

              <FormEditPostTitle topic={topic} onCancel={() => setIsEditingTitle((prev) => !prev)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Link href="/irpf/forum" className="flex text-sm font-bold italic text-blue-600 hover:underline">
        ← Voltar
      </Link>
    </div>
  );
}

interface IForumTopimCardProps {
  topic: NonNullable<IPostRepliesQuery["postByShortId"]> | IPostReplyFieldsFragment;
  children?: React.ReactNode;
  className?: string;
  postShortId: string;
  url: string;
  onReply?: () => void;
}

export function ForumTopicCard({ topic, children, className, postShortId, url, onReply }: IForumTopimCardProps) {
  const selfie = useSelfie();
  const router = useRouter();
  const {
    isDestroyingPost,
    isUnpinningPost,
    isPinningPost,
    isUnlockingPost,
    isLockingPost,
    isDestroyingPostReply,
    isPinningPostReply,
    isUnpinningPostReply,
    isSettingAnswerPostReply,
    isUnsettingAnswerPostReply,

    destroyPostReply,
    destroyPost,
    pinPost,
    unpinPost,
    unlockPost,
    lockPost,
    pinPostReply,
    unpinPostReply,
    setPostReplyAnswer,
    unsetPostReplyAnswer,
  } = usePostActions();
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isOpenShare, setIsOpenShare] = useState<boolean>(false);

  const viewerCanTakeActions = topic.viewerCanEdit || topic.viewerCanDestroy || (selfie && selfie.isSiteAdmin);
  const viewerCanTakeAnswerActions = topic.viewerDidAuthor || (selfie && selfie.isSiteAdmin);

  const loadingAnswer = isSettingAnswerPostReply || isUnsettingAnswerPostReply;

  const refetchQueries: InternalRefetchQueriesInclude = [
    {
      query: PostRepliesQuery,
      variables: {
        postShortId,
        cursor: null,
      },
    },
    {
      query: PostsQuery,
      variables: {
        contextKey: getYear(new Date()).toString(),
        contextType: PostContextType.IrpfForum,
        limit: 30,
        cursor: null,
      },
    },
  ];

  return (
    <div
      className={classNames(
        "group relative col-span-4 rounded md:col-span-9",
        "border border-neutral-50 bg-white px-4 py-8 shadow md:p-8",
        {
          "border-green-500": topic.__typename === "PostReply" && topic.answeredAt,
        },
        className,
      )}
      id={`reply-${topic._id}-${parseISO(topic.createdAt).getTime()}`}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-5 gap-y-4 font-aeonik">
        <Avatar
          key={topic.author._id}
          src={topic.author.pictureUrl}
          hashValue={topic.author._id}
          alt={topic.author.firstName}
          size={42}
          className="text-2xl"
        />

        <div className="flex flex-col">
          <span className="w-max text-sm font-bold text-gray-800">{topic.author.fullName}</span>
          {topic.author.isSiteAdmin && <span className="text-xs  text-gray-500">Moderador</span>}
        </div>

        <div className="flex items-center space-x-2">
          <div className="space-x-0.5">{topic.isPinned && <Pin className="h-4 w-4 fill-blue-50" />}</div>

          <RelativeTime className="font-medium text-gray-500" timeStamp={parseISO(topic.createdAt)} />

          {topic.__typename === "IrpfPost" && topic.replies.nodes.length > 0 && (
            <>
              <span className="font-medium text-gray-500">•</span>
              <span className="font-medium text-gray-500">
                {topic.replies.nodes.length} {topic.replies.nodes.length > 1 ? "respostas" : "resposta"}
              </span>
            </>
          )}
        </div>

        {!isEditingContent && (
          <div className="col-start-2">
            <Editor editable={false} initialState={topic.bodyAsJson} />
          </div>
        )}

        {selfie && isEditingContent && topic.viewerCanEdit && (
          <div className="col-start-2">
            <ForumEditPost
              onCancel={() => setIsEditingContent(!isEditingContent)}
              shortId={postShortId}
              id={topic._id}
              initialState={topic.bodyAsJson}
              isReply={topic.__typename === "PostReply"}
            />
          </div>
        )}

        <div className={classNames("col-span-2 col-start-2 my-2 grid grid-cols-[1fr_auto] items-start")}>
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:gap-2">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-2">
              {topic.__typename === "PostReply" && topic.answeredAt && (
                <div className="flex w-fit rounded-full border border-gray-300 bg-green-200 p-1 pl-0.5">
                  <div className="flex w-fit items-center space-x-1 text-green-600">
                    {topic.answeredAt && <CheckCircleIcon className="h-5 w-5 fill-green-600" />}

                    <span className="flex font-aeonik text-xs font-bold tracking-wide">Marcado como resposta</span>
                  </div>
                </div>
              )}

              <PostReactions
                reactable={topic}
                refetchQueries={refetchQueries}
                subjectId={topic._id}
                subjectType={topic.__typename === "IrpfPost" ? IReactionSubject.Post : IReactionSubject.PostReply}
              />
            </div>

            <div className="flex gap-2">
              {onReply && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild={true}>
                      <button
                        type="button"
                        onClick={onReply}
                        className="flex h-fit items-center rounded-lg border border-neutral-200 p-1 hover:border-blue-300 hover:bg-blue-50"
                      >
                        <Reply className="h-5 w-5 text-gray-500 hover:text-blue-500" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      sideOffset={12}
                      className="max-w-xs rounded-xl border border-transparent bg-black text-white"
                    >
                      <strong className="font-bold">Responder</strong>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <div className="flex space-x-5 group-hover:flex md:hidden">
                {viewerCanTakeActions && (
                  <CommentActionsDropdown>
                    {({ closeOnClick }) => (
                      <Menu>
                        {topic.viewerCanEdit && (
                          <MenuActionButtonItem
                            intent="primary"
                            onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                          >
                            Editar
                          </MenuActionButtonItem>
                        )}

                        <MenuActionButtonItem
                          intent="primary"
                          onClick={closeOnClick(() => setIsOpenShare((prev) => !prev))}
                        >
                          Compartilhar
                        </MenuActionButtonItem>

                        {selfie && selfie.isSiteAdmin && (
                          <>
                            <MenuActionButtonItem
                              intent="primary"
                              onClick={closeOnClick(() => {
                                navigator.clipboard.writeText(topic.author.email);
                              })}
                            >
                              Copiar email
                            </MenuActionButtonItem>

                            <MenuDivider />

                            <MenuTitle>Moderação</MenuTitle>
                          </>
                        )}

                        <MenuDivider />

                        {topic.viewerCanDestroy && (
                          <MenuActionButtonItem
                            intent="danger"
                            disabled={isDestroyingPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                destroyPostReply({
                                  variables: {
                                    replyId: topic._id,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                destroyPost({
                                  variables: {
                                    postId: topic._id,
                                  },
                                  refetchQueries,
                                  onCompleted: () => router.push("/irpf/forum"),
                                });
                              }
                            }}
                          >
                            {isDestroyingPost || isDestroyingPostReply ? "Apagando..." : "Apagar"}
                          </MenuActionButtonItem>
                        )}

                        {topic.isPinned ? (
                          <MenuActionButtonItem
                            disabled={isUnpinningPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                unpinPostReply({
                                  variables: {
                                    replyId: topic._id,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                unpinPost({
                                  variables: {
                                    postId: topic._id,
                                  },
                                  refetchQueries,
                                });
                              }
                            }}
                          >
                            {isUnpinningPost || isUnpinningPostReply ? "Desfixando..." : "Desfixar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem
                            disabled={isPinningPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                pinPostReply({
                                  variables: {
                                    replyId: topic._id,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                pinPost({
                                  variables: {
                                    postId: topic._id,
                                  },
                                  refetchQueries,
                                });
                              }
                            }}
                          >
                            {isPinningPost || isPinningPostReply ? "Fixando..." : "Fixar"}
                          </MenuActionButtonItem>
                        )}

                        {topic.__typename === "IrpfPost" && selfie && selfie.isSiteAdmin && (
                          <>
                            {topic.isLocked && (
                              <MenuActionButtonItem
                                disabled={isUnlockingPost}
                                onClick={() =>
                                  unlockPost({
                                    variables: {
                                      postId: topic._id,
                                    },
                                    refetchQueries,
                                  })
                                }
                              >
                                {isUnlockingPost ? "Destrancando..." : "Destrancar"}
                              </MenuActionButtonItem>
                            )}

                            {!topic.isLocked && (
                              <MenuActionButtonItem
                                disabled={isLockingPost}
                                onClick={() =>
                                  lockPost({
                                    variables: {
                                      postId: topic._id,
                                    },
                                    refetchQueries,
                                  })
                                }
                              >
                                {isLockingPost ? "Trancando..." : "Trancar"}
                              </MenuActionButtonItem>
                            )}
                          </>
                        )}

                        {topic.__typename === "PostReply" && viewerCanTakeAnswerActions && (
                          <>
                            {topic.answeredAt && (
                              <MenuActionButtonItem
                                disabled={isUnsettingAnswerPostReply}
                                onClick={() => {
                                  unsetPostReplyAnswer({
                                    variables: {
                                      replyId: topic._id,
                                    },
                                    refetchQueries,
                                  });
                                }}
                              >
                                {loadingAnswer ? "Desmarcando..." : "Desmarcar como resposta"}
                              </MenuActionButtonItem>
                            )}

                            {!topic.answeredAt && (
                              <MenuActionButtonItem
                                disabled={isSettingAnswerPostReply}
                                onClick={() => {
                                  setPostReplyAnswer({
                                    variables: {
                                      replyId: topic._id,
                                    },
                                    refetchQueries,
                                  });
                                }}
                              >
                                {loadingAnswer ? "Marcando..." : "Marcar como resposta"}
                              </MenuActionButtonItem>
                            )}
                          </>
                        )}
                      </Menu>
                    )}
                  </CommentActionsDropdown>
                )}
              </div>
            </div>
          </div>
        </div>

        {children}
      </div>

      {topic.__typename === "PostReply" && topic.answeredBy && (
        <div className="absolute -bottom-6 left-4 z-10">
          <span className="font-aeonik text-sm font-medium text-green-600">
            Selecionado como reposta por <strong>{topic.answeredBy.fullName}</strong>
          </span>
        </div>
      )}

      <ForumShareDialog url={url} open={isOpenShare} toggle={() => setIsOpenShare((prev) => !prev)} />
    </div>
  );
}

interface IForumTopicQuestionCardProps {
  topic: NonNullable<IPostRepliesQuery["postByShortId"]>;
  onReply: () => void;
}

export function ForumTopicQuestionCard({ topic, onReply }: IForumTopicQuestionCardProps) {
  if (topic.__typename !== "IrpfPost") {
    return null;
  }

  return (
    <ForumTopicCard
      topic={topic}
      className="border border-brand"
      postShortId={topic.shortId}
      url={topic.url}
      onReply={onReply}
    >
      {topic.replyAnswer && (
        <div key={`reply-answer/${topic.replyAnswer.shortId}`} className="col-span-3 -mx-4 -mb-8 md:-mx-8">
          <div className="col-span-3 flex w-full flex-col space-y-5 rounded-bl rounded-br bg-green-200 p-4 md:px-8">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-8 w-8 fill-green-600" />

              <span className="font-aeonik text-sm font-medium text-green-600">
                Respondido por <strong className="font-black">{topic.replyAnswer.author.fullName}</strong>
              </span>

              <div className="flex space-x-1">
                <span className="font-aeonik text-sm text-gray-600">em</span>
                <span className="font-aeonik text-sm capitalize text-gray-600">
                  {format(parseISO(topic.replyAnswer.createdAt), "LLLL dd, yyyy", { locale: ptBR })}
                </span>
              </div>
            </div>

            <div
              style={{
                WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 12px), transparent 100%)",
              }}
            >
              <Editor
                className="max-h-32 overflow-hidden bg-inherit"
                editable={false}
                initialState={topic.replyAnswer.bodyAsJson}
              />
            </div>

            <Link
              as={`/irpf/forum/${topic.shortId}/replies/${topic.replyAnswer.shortId}`}
              href="/irpf/forum/[shortId]/replies/[replyShortId]"
              className="flex space-x-1 text-blue-600"
            >
              <span className="font-aeonik text-sm font-medium hover:underline">Ver resposta</span>
              <ArrowLongRightIcon className="h-5 w-5" />
            </Link>
          </div>
        </div>
      )}
    </ForumTopicCard>
  );
}

interface IForumShareDialogProps {
  url: string;
  open: boolean;
  toggle: () => void;
}

export function ForumShareDialog({ url, open, toggle }: IForumShareDialogProps) {
  const [isCopied, setIsCopied] = useState(false);

  return (
    <Dialog open={open} onOpenChange={toggle}>
      <DialogContent className="max-h-screen sm:max-w-[425px] lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Compartilhar tópico</DialogTitle>
        </DialogHeader>

        <div className="flex items-center space-x-4">
          <TextareaAutosize
            readOnly={true}
            value={url}
            className={classNames(
              "box-border flex h-10 w-full resize-none appearance-none rounded font-monospace",
              "border-none bg-slate-200 focus:ring-0",
            )}
          />

          <button
            type="button"
            className="h-10 rounded border bg-blue-500 p-1 px-2 text-white hover:bg-blue-600"
            onClick={() => {
              navigator.clipboard.writeText(url);
              setIsCopied((previous) => !previous);
            }}
          >
            {isCopied && <CheckIcon className="h-6 w-6" />}
            {!isCopied && <ClipboardDocumentIcon className="h-6 w-6" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ForumSkeleton() {
  return (
    <ForumContainer>
      <div className="col-span-4 animate-pulse space-y-0.5 font-aeonik md:col-span-12">
        <div className="h-8 w-52 bg-gray-300" />
        <div className="h-5 w-12 bg-gray-300" />
      </div>

      <ForumTopicCardSekeleton className="h-96" />

      {Array.from({ length: 2 }).map(() => (
        <ForumTopicCardSekeleton />
      ))}
    </ForumContainer>
  );
}

interface IForumTopicCardSekeletonProps {
  className?: string;
}

export function ForumTopicCardSekeleton({ className }: IForumTopicCardSekeletonProps) {
  return (
    <div
      className={classNames(
        "col-span-4 h-72 w-full animate-pulse rounded bg-gray-200",
        "px-4 py-8 md:col-span-9 md:p-8",
        className,
      )}
    >
      <div className="grid h-full grid-cols-[auto_1fr_auto] items-start gap-x-5 gap-y-4">
        <div className="h-10 w-10 rounded-full bg-gray-300" />

        <div className="flex h-full w-full flex-col space-y-5">
          <div className="flex flex-col space-y-0.5">
            <div className="flex h-4 w-36 rounded bg-gray-300" />
            <div className="flex h-3 w-24 rounded bg-gray-300" />
          </div>

          <div className="flex h-5/6 w-full rounded bg-gray-300" />
        </div>

        <div className="h-3 w-10 rounded bg-gray-300" />
      </div>
    </div>
  );
}
